import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, setDoc } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';
import { FaEnvelope, FaEdit, FaTimes, FaSave, FaChevronDown, FaChevronRight, FaPlus, FaTrash } from 'react-icons/fa';
import { Dialog, DialogBackdrop, DialogPanel } from '@headlessui/react';
import { toast } from 'react-toastify';
import { toUserMessage } from '../utils/userError';

const EmailRouting = ({ userInfo, organization = 'alliedoms' }) => {
  const [routingConfig, setRoutingConfig] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [editFormData, setEditFormData] = useState({
    routingMode: 'category',
    categoryEmail: { to: '', cc: '', bcc: '' },
    formEmails: {}
  });
  
  // For multi-select mode
  const [selectedForms, setSelectedForms] = useState([]);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignEmail, setAssignEmail] = useState({ to: '', cc: '', bcc: '' });
  
  // Helper to get organization-specific document ID
  const getDocId = (categoryId) => `${organization}_${categoryId}`;

  // Define form categories with their sub-forms
  const formCategories = [
    { 
      id: 'complaints', 
      label: 'Patient Complaints',
      forms: [
        { id: 'clinicalCare', label: 'Clinical Care' },
        { id: 'customerService', label: 'Customer Service' },
        { id: 'billingFinancial', label: 'Billing/Financial' },
        { id: 'facilityEnvironment', label: 'Facility/Environment' }
      ]
    },
    { 
      id: 'complications', 
      label: 'Complications',
      forms: [
        { id: 'anesthesia', label: 'Anesthesia Complications' },
        { id: 'dentalImplant', label: 'Dental Implant Complications' },
        { id: 'dentoalveolarSurgery', label: 'Dentoalveolar Surgery Complications' },
        { id: 'needleStickInjury', label: 'Needle Stick Injury' }
      ]
    },
    { 
      id: 'hipaa', 
      label: 'HIPAA Disclosures',
      forms: [
        { id: 'unauthorizedDisclosure', label: 'Unauthorized Disclosure' },
        { id: 'improperAccess', label: 'Improper Access' },
        { id: 'lostRecords', label: 'Lost/Misplaced Records' },
        { id: 'vendorBreach', label: 'Vendor/BAA Breach' },
        { id: 'patientRights', label: 'Patient Rights Violation' },
        { id: 'transmissionError', label: 'Transmission Error' }
      ]
    },
    { 
      id: 'itBreach', 
      label: 'IT Security Events',
      forms: [
        { id: 'dataEvent', label: 'Data Event' },
        { id: 'ransomwareEvent', label: 'Ransomware Event' },
        { id: 'cyberEvent', label: 'Cyber Event' },
        { id: 'systemEvent', label: 'System Event' },
        { id: 'emailEvent', label: 'Email Event' },
        { id: 'deviceEvent', label: 'Device Event' }
      ]
    },
    { 
      id: 'equipment', 
      label: 'Equipment Malfunction',
      forms: [
        { id: 'surgical', label: 'Surgical Equipment' },
        { id: 'imaging', label: 'Imaging Equipment' },
        { id: 'sterilization', label: 'Sterilization Equipment' },
        { id: 'anesthesiaEquipment', label: 'Anesthesia Equipment' },
        { id: 'computerSoftware', label: 'Computer/Software Systems' },
        { id: 'facilitySystems', label: 'Facility Systems' }
      ]
    },
    { 
      id: 'scheduling', 
      label: 'Scheduling/Communication',
      forms: [
        { id: 'appointment', label: 'Appointment Scheduling' },
        { id: 'patientCommunication', label: 'Patient Communication' },
        { id: 'documentation', label: 'Documentation Error' },
        { id: 'systemProcess', label: 'System/Process Error' }
      ]
    },
    { 
      id: 'emergency', 
      label: 'Emergency Reports',
      forms: [
        { id: 'emergency', label: 'Emergency Report' }
      ]
    },
    { 
      id: 'aggressivePatient', 
      label: 'Aggressive Patient Interactions',
      forms: [
        { id: 'aggressivePatient', label: 'Aggressive Patient Report' }
      ]
    }
  ];

  useEffect(() => {
    fetchRoutingConfig();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization]);

  const fetchRoutingConfig = async () => {
    try {
      const routingCollection = collection(db, 'emailRouting');
      const snapshot = await getDocs(routingCollection);
      const config = {};
      
      // Filter documents that belong to this organization
      const orgPrefix = `${organization}_`;
      snapshot.forEach(docSnap => {
        if (docSnap.id.startsWith(orgPrefix)) {
          // Strip the organization prefix to get the category ID
          const categoryId = docSnap.id.replace(orgPrefix, '');
          config[categoryId] = docSnap.data();
        }
      });
      
      setRoutingConfig(config);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching email routing:', error);
      setLoading(false);
    }
  };

  const handleSaveRouting = async () => {
    if (!editingCategory) return;
    
    setSaving(true);
    
    try {
      // Use organization-specific document ID
      const routingRef = doc(db, 'emailRouting', getDocId(editingCategory));
      
      if (editFormData.routingMode === 'category') {
        const toEmails = editFormData.categoryEmail.to
          .split(',')
          .map(email => email.trim())
          .filter(email => email && email.includes('@'));
        
        const ccEmails = (editFormData.categoryEmail.cc || '')
          .split(',')
          .map(email => email.trim())
          .filter(email => email && email.includes('@'));
        
        const bccEmails = (editFormData.categoryEmail.bcc || '')
          .split(',')
          .map(email => email.trim())
          .filter(email => email && email.includes('@'));

        if (toEmails.length === 0) {
          toast.error('Please enter at least one valid email address in the "To" field.');
          setSaving(false);
          return;
        }

        await setDoc(routingRef, {
          routingMode: 'category',
          to: toEmails,
          cc: ccEmails.length > 0 ? ccEmails : [],
          bcc: bccEmails.length > 0 ? bccEmails : [],
          updatedAt: new Date(),
          updatedBy: userInfo.email
        });
        
        setRoutingConfig({
          ...routingConfig,
          [editingCategory]: {
            routingMode: 'category',
            to: toEmails,
            cc: ccEmails,
            bcc: bccEmails
          }
        });
      } else {
        // Form-level routing
        const forms = {};
        let hasValidForm = false;
        
        for (const [formId, emailData] of Object.entries(editFormData.formEmails)) {
          if (emailData.to && emailData.to.length > 0) {
            hasValidForm = true;
            forms[formId] = {
              to: Array.isArray(emailData.to) ? emailData.to : emailData.to.split(',').map(e => e.trim()).filter(e => e && e.includes('@')),
              cc: emailData.cc ? (Array.isArray(emailData.cc) ? emailData.cc : emailData.cc.split(',').map(e => e.trim()).filter(e => e && e.includes('@'))) : [],
              bcc: emailData.bcc ? (Array.isArray(emailData.bcc) ? emailData.bcc : emailData.bcc.split(',').map(e => e.trim()).filter(e => e && e.includes('@'))) : []
            };
          }
        }

        if (!hasValidForm) {
          toast.error('Please assign at least one email to a form.');
          setSaving(false);
          return;
        }

        await setDoc(routingRef, {
          routingMode: 'form',
          forms,
          updatedAt: new Date(),
          updatedBy: userInfo.email
        });
        
        setRoutingConfig({
          ...routingConfig,
          [editingCategory]: {
            routingMode: 'form',
            forms
          }
        });
      }
      
      setEditingCategory(null);
      setEditFormData({
        routingMode: 'category',
        categoryEmail: { to: '', cc: '', bcc: '' },
        formEmails: {}
      });
      setSelectedForms([]);
      setSaving(false);
      toast.success('Email routing saved successfully!');
    } catch (error) {
      console.error('Error saving email routing:', error);
      toast.error(toUserMessage(error, 'Unable to save email routing. Please try again.'));
      setSaving(false);
    }
  };

  const handleEditCategory = (categoryId) => {
    const existing = routingConfig[categoryId];
    const category = formCategories.find(c => c.id === categoryId);
    
    const initialFormEmails = {};
    category?.forms.forEach(form => {
      if (existing?.routingMode === 'form' && existing?.forms?.[form.id]) {
        initialFormEmails[form.id] = {
          to: existing.forms[form.id].to || [],
          cc: existing.forms[form.id].cc || [],
          bcc: existing.forms[form.id].bcc || []
        };
      }
    });
    
    setEditingCategory(categoryId);
    setEditFormData({
      routingMode: existing?.routingMode || 'category',
      categoryEmail: {
        to: existing?.to?.join(', ') || '',
        cc: existing?.cc?.join(', ') || '',
        bcc: existing?.bcc?.join(', ') || ''
      },
      formEmails: initialFormEmails
    });
    setSelectedForms([]);
  };

  const handleCancelEdit = () => {
    setEditingCategory(null);
    setEditFormData({
      routingMode: 'category',
      categoryEmail: { to: '', cc: '', bcc: '' },
      formEmails: {}
    });
    setSelectedForms([]);
    setShowAssignModal(false);
  };

  const toggleCategoryExpand = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  const toggleFormSelection = (formId) => {
    setSelectedForms(prev => 
      prev.includes(formId) 
        ? prev.filter(id => id !== formId)
        : [...prev, formId]
    );
  };

  const selectAllForms = () => {
    const category = formCategories.find(c => c.id === editingCategory);
    if (category) {
      const allFormIds = category.forms.map(f => f.id);
      const unconfiguredForms = allFormIds.filter(id => !editFormData.formEmails[id]?.to?.length);
      setSelectedForms(unconfiguredForms);
    }
  };

  const clearSelection = () => {
    setSelectedForms([]);
  };

  const handleAssignEmailToSelected = () => {
    const toEmails = assignEmail.to
      .split(',')
      .map(email => email.trim())
      .filter(email => email && email.includes('@'));
    
    if (toEmails.length === 0) {
      toast.error('Please enter at least one valid email address.');
      return;
    }

    const ccEmails = (assignEmail.cc || '')
      .split(',')
      .map(email => email.trim())
      .filter(email => email && email.includes('@'));
    
    const bccEmails = (assignEmail.bcc || '')
      .split(',')
      .map(email => email.trim())
      .filter(email => email && email.includes('@'));

    const updatedFormEmails = { ...editFormData.formEmails };
    selectedForms.forEach(formId => {
      updatedFormEmails[formId] = {
        to: toEmails,
        cc: ccEmails,
        bcc: bccEmails
      };
    });

    setEditFormData({
      ...editFormData,
      formEmails: updatedFormEmails
    });
    setSelectedForms([]);
    setShowAssignModal(false);
    setAssignEmail({ to: '', cc: '', bcc: '' });
    toast.success(`Email assigned to ${selectedForms.length} form(s)`);
  };

  const removeFormFromGroup = (formId) => {
    const updatedFormEmails = { ...editFormData.formEmails };
    delete updatedFormEmails[formId];
    setEditFormData({
      ...editFormData,
      formEmails: updatedFormEmails
    });
  };

  const getRoutingStatus = (categoryId) => {
    const config = routingConfig[categoryId];
    if (!config) return { configured: false, mode: null };
    
    if (config.routingMode === 'form') {
      const configuredForms = Object.keys(config.forms || {}).length;
      return { 
        configured: configuredForms > 0, 
        mode: 'form',
        formCount: configuredForms
      };
    }
    
    return { 
      configured: config.to?.length > 0, 
      mode: 'category',
      emails: config.to 
    };
  };

  // Group forms by their assigned email
  const getFormGroups = () => {
    const groups = {};
    const category = formCategories.find(c => c.id === editingCategory);
    
    if (!category) return { groups: {}, unconfigured: [] };

    const unconfigured = [];
    
    category.forms.forEach(form => {
      const config = editFormData.formEmails[form.id];
      if (config?.to?.length > 0) {
        const key = config.to.join(', ');
        if (!groups[key]) {
          groups[key] = {
            to: config.to,
            cc: config.cc || [],
            bcc: config.bcc || [],
            forms: []
          };
        }
        groups[key].forms.push(form);
      } else {
        unconfigured.push(form);
      }
    });

    return { groups, unconfigured };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="inline-block size-8 animate-spin rounded-full border-4 border-solid border-indigo-600 border-r-transparent"></div>
          <p className="mt-4 text-sm text-gray-600">Loading email routing configuration...</p>
        </div>
      </div>
    );
  }

  const editingCategoryData = formCategories.find(c => c.id === editingCategory);
  const { groups: formGroups, unconfigured: unconfiguredForms } = getFormGroups();

  return (
    <div className="[&_*:focus]:outline-none [&_*:focus-visible]:outline [&_*:focus-visible]:outline-2 [&_*:focus-visible]:outline-offset-2 [&_*:focus-visible]:outline-indigo-600">
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <FaEnvelope className="size-6 text-indigo-600" />
          <h2 className="text-base font-semibold text-gray-900">Email Routing Configuration</h2>
        </div>
        <p className="text-sm text-gray-500">
          Configure email recipients for different form categories. You can set one email for an entire category or group forms and assign different emails.
        </p>
      </div>

      {/* Categories List */}
      <div className="space-y-3">
        {formCategories.map(category => {
          const status = getRoutingStatus(category.id);
          const isExpanded = expandedCategories[category.id];
          const config = routingConfig[category.id];
          
          return (
            <div key={category.id} className="rounded-lg border border-gray-200 bg-white shadow-sm overflow-hidden">
              <div className="flex items-center justify-between px-4 py-4 hover:bg-gray-50 transition-colors">
                <div className="flex items-center gap-3 flex-1">
                  <button
                    onClick={() => toggleCategoryExpand(category.id)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    {isExpanded ? (
                      <FaChevronDown className="size-4" />
                    ) : (
                      <FaChevronRight className="size-4" />
                    )}
                  </button>
                  <div className="flex-1">
                    <div className="font-medium text-gray-900">{category.label}</div>
                    <div className="text-sm text-gray-500">
                      {status.configured ? (
                        status.mode === 'category' ? (
                          <span className="text-green-600">
                            Category routing: {status.emails?.join(', ')}
                          </span>
                        ) : (
                          <span className="text-blue-600">
                            Form-specific routing: {status.formCount} form(s) configured
                          </span>
                        )
                      ) : (
                        <span className="text-gray-400 italic">Not configured</span>
                      )}
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => handleEditCategory(category.id)}
                  className="ml-4 rounded-md bg-indigo-50 px-3 py-2 text-sm font-semibold text-indigo-600 shadow-sm ring-1 ring-inset ring-indigo-600/20 hover:bg-indigo-100 transition-colors"
                >
                  <FaEdit className="inline size-4 mr-2" />
                  Edit
                </button>
              </div>
              
              {isExpanded && (
                <div className="border-t border-gray-100 bg-gray-50 px-4 py-3">
                  <div className="text-xs font-medium text-gray-500 uppercase mb-2">Form Types in this Category</div>
                  <div className="space-y-2">
                    {category.forms.map(form => {
                      const formConfig = config?.routingMode === 'form' ? config.forms?.[form.id] : null;
                      const usesCategory = config?.routingMode === 'category' && config?.to?.length > 0;
                      
                      return (
                        <div key={form.id} className="flex items-center justify-between py-1 text-sm">
                          <span className="text-gray-700">{form.label}</span>
                          <span className={`text-xs ${formConfig ? 'text-blue-600' : usesCategory ? 'text-green-600' : 'text-gray-400'}`}>
                            {formConfig ? (
                              formConfig.to?.join(', ')
                            ) : usesCategory ? (
                              '→ Uses category email'
                            ) : (
                              'Not configured'
                            )}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Edit Modal */}
      <Dialog open={!!editingCategory} onClose={handleCancelEdit} className="relative z-50">
        <DialogBackdrop
          transition
          className="fixed inset-0 bg-gray-500/75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
        />
        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <DialogPanel
              transition
              className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-2xl data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
            >
              <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4 max-h-[80vh] overflow-y-auto">
                <div className="absolute right-0 top-0 pr-4 pt-4">
                  <button
                    type="button"
                    onClick={handleCancelEdit}
                    className="rounded-md bg-white text-gray-400 hover:text-gray-500"
                  >
                    <span className="sr-only">Close</span>
                    <FaTimes aria-hidden="true" className="size-6" />
                  </button>
                </div>
                
                <div className="sm:flex sm:items-start mb-6">
                  <div className="mx-auto flex size-12 shrink-0 items-center justify-center rounded-full bg-indigo-100 sm:mx-0 sm:size-10">
                    <FaEnvelope aria-hidden="true" className="size-6 text-indigo-600" />
                  </div>
                  <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                    <h3 className="text-base font-semibold text-gray-900">
                      Edit Email Routing
                    </h3>
                    <p className="text-sm text-gray-500">
                      {editingCategoryData?.label}
                    </p>
                  </div>
                </div>

                {/* Routing Mode Selection */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-900 mb-3">Routing Mode</label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => setEditFormData({ ...editFormData, routingMode: 'category' })}
                      className={`relative rounded-lg border p-4 text-left transition-all ${
                        editFormData.routingMode === 'category'
                          ? 'border-indigo-600 bg-indigo-50 ring-2 ring-indigo-600'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="font-medium text-gray-900">Category Email</div>
                      <div className="text-xs text-gray-500 mt-1">One email for all forms</div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditFormData({ ...editFormData, routingMode: 'form' })}
                      className={`relative rounded-lg border p-4 text-left transition-all ${
                        editFormData.routingMode === 'form'
                          ? 'border-indigo-600 bg-indigo-50 ring-2 ring-indigo-600'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="font-medium text-gray-900">Form Groups</div>
                      <div className="text-xs text-gray-500 mt-1">Group forms by email</div>
                    </button>
                  </div>
                </div>

                {/* Category Email Configuration */}
                {editFormData.routingMode === 'category' && (
                  <div className="space-y-4 border-t border-gray-200 pt-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-900 mb-1">
                        To <span className="text-red-500">*</span>
                        <span className="text-gray-500 font-normal ml-1">(comma-separated)</span>
                      </label>
                      <input
                        type="text"
                        value={editFormData.categoryEmail.to}
                        onChange={(e) => setEditFormData({
                          ...editFormData,
                          categoryEmail: { ...editFormData.categoryEmail, to: e.target.value }
                        })}
                        placeholder="email1@example.com, email2@example.com"
                        className="block w-full rounded-md bg-white px-3 py-2 text-sm text-gray-900 border border-gray-300 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:border-transparent"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-900 mb-1">CC</label>
                        <input
                          type="text"
                          value={editFormData.categoryEmail.cc}
                          onChange={(e) => setEditFormData({
                            ...editFormData,
                            categoryEmail: { ...editFormData.categoryEmail, cc: e.target.value }
                          })}
                          placeholder="cc@example.com"
                          className="block w-full rounded-md bg-white px-3 py-2 text-sm text-gray-900 border border-gray-300 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-900 mb-1">BCC</label>
                        <input
                          type="text"
                          value={editFormData.categoryEmail.bcc}
                          onChange={(e) => setEditFormData({
                            ...editFormData,
                            categoryEmail: { ...editFormData.categoryEmail, bcc: e.target.value }
                          })}
                          placeholder="bcc@example.com"
                          className="block w-full rounded-md bg-white px-3 py-2 text-sm text-gray-900 border border-gray-300 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:border-transparent"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Form Groups Configuration */}
                {editFormData.routingMode === 'form' && editingCategoryData && (
                  <div className="border-t border-gray-200 pt-4">
                    {/* Configured Groups */}
                    {Object.keys(formGroups).length > 0 && (
                      <div className="mb-6">
                        <div className="text-sm font-medium text-gray-900 mb-3">Configured Email Groups</div>
                        <div className="space-y-3">
                          {Object.entries(formGroups).map(([email, group], idx) => (
                            <div key={idx} className="bg-green-50 border border-green-200 rounded-lg p-3">
                              <div className="flex items-start justify-between mb-2">
                                <div>
                                  <div className="text-sm font-medium text-green-800">{email}</div>
                                  {group.cc?.length > 0 && (
                                    <div className="text-xs text-green-600">CC: {group.cc.join(', ')}</div>
                                  )}
                                  {group.bcc?.length > 0 && (
                                    <div className="text-xs text-green-600">BCC: {group.bcc.join(', ')}</div>
                                  )}
                                </div>
                              </div>
                              <div className="flex flex-wrap gap-2">
                                {group.forms.map(form => (
                                  <span
                                    key={form.id}
                                    className="inline-flex items-center gap-1 px-2 py-1 bg-white border border-green-300 rounded text-xs text-green-800"
                                  >
                                    {form.label}
                                    <button
                                      onClick={() => removeFormFromGroup(form.id)}
                                      className="text-green-600 hover:text-red-600"
                                    >
                                      <FaTimes className="size-3" />
                                    </button>
                                  </span>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Unconfigured Forms */}
                    {unconfiguredForms.length > 0 && (
                      <div>
                        <div className="flex items-center justify-between mb-3">
                          <div className="text-sm font-medium text-gray-900">
                            Select Forms to Assign Email
                          </div>
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={selectAllForms}
                              className="text-xs text-indigo-600 hover:text-indigo-800"
                            >
                              Select All
                            </button>
                            {selectedForms.length > 0 && (
                              <button
                                type="button"
                                onClick={clearSelection}
                                className="text-xs text-gray-500 hover:text-gray-700"
                              >
                                Clear
                              </button>
                            )}
                          </div>
                        </div>
                        
                        <div className="bg-gray-50 rounded-lg p-3 mb-4">
                          <div className="grid grid-cols-2 gap-2">
                            {unconfiguredForms.map(form => (
                              <label
                                key={form.id}
                                className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-colors ${
                                  selectedForms.includes(form.id)
                                    ? 'bg-indigo-100 border border-indigo-300'
                                    : 'bg-white border border-gray-200 hover:border-gray-300'
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={selectedForms.includes(form.id)}
                                  onChange={() => toggleFormSelection(form.id)}
                                  className="size-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                                />
                                <span className="text-sm text-gray-700">{form.label}</span>
                              </label>
                            ))}
                          </div>
                        </div>

                        {selectedForms.length > 0 && (
                          <button
                            type="button"
                            onClick={() => setShowAssignModal(true)}
                            className="w-full flex items-center justify-center gap-2 rounded-md bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500"
                          >
                            <FaPlus className="size-4" />
                            Assign Email to {selectedForms.length} Selected Form(s)
                          </button>
                        )}
                      </div>
                    )}

                    {unconfiguredForms.length === 0 && Object.keys(formGroups).length > 0 && (
                      <div className="text-center py-4 text-sm text-gray-500">
                        All forms have been assigned to email groups.
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                <button
                  type="button"
                  onClick={handleSaveRouting}
                  disabled={saving}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed sm:ml-3 sm:w-auto"
                >
                  {saving ? (
                    <>
                      <svg className="animate-spin size-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>Saving...</span>
                    </>
                  ) : (
                    <>
                      <FaSave className="size-4" />
                      <span>Save</span>
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={handleCancelEdit}
                  className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-4 py-2.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                >
                  Cancel
                </button>
              </div>
            </DialogPanel>
          </div>
        </div>
      </Dialog>

      {/* Assign Email Modal */}
      <Dialog open={showAssignModal} onClose={() => setShowAssignModal(false)} className="relative z-[60]">
        <DialogBackdrop className="fixed inset-0 bg-gray-500/75" />
        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <DialogPanel className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl sm:w-full sm:max-w-md">
              <div className="bg-white px-4 pb-4 pt-5 sm:p-6">
                <div className="mb-4">
                  <h3 className="text-base font-semibold text-gray-900">
                    Assign Email to {selectedForms.length} Form(s)
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">
                    {editingCategoryData?.forms.filter(f => selectedForms.includes(f.id)).map(f => f.label).join(', ')}
                  </p>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-900 mb-1">
                      To <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={assignEmail.to}
                      onChange={(e) => setAssignEmail({ ...assignEmail, to: e.target.value })}
                      placeholder="email@example.com"
                      className="block w-full rounded-md bg-white px-3 py-2 text-sm text-gray-900 border border-gray-300 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-600"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-900 mb-1">CC</label>
                      <input
                        type="text"
                        value={assignEmail.cc}
                        onChange={(e) => setAssignEmail({ ...assignEmail, cc: e.target.value })}
                        placeholder="cc@example.com"
                        className="block w-full rounded-md bg-white px-3 py-2 text-sm text-gray-900 border border-gray-300 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-600"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-900 mb-1">BCC</label>
                      <input
                        type="text"
                        value={assignEmail.bcc}
                        onChange={(e) => setAssignEmail({ ...assignEmail, bcc: e.target.value })}
                        placeholder="bcc@example.com"
                        className="block w-full rounded-md bg-white px-3 py-2 text-sm text-gray-900 border border-gray-300 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-600"
                      />
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                <button
                  type="button"
                  onClick={handleAssignEmailToSelected}
                  className="inline-flex w-full justify-center rounded-md bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 sm:ml-3 sm:w-auto"
                >
                  Assign Email
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowAssignModal(false);
                    setAssignEmail({ to: '', cc: '', bcc: '' });
                  }}
                  className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-4 py-2.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                >
                  Cancel
                </button>
              </div>
            </DialogPanel>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default EmailRouting;
